import { isDataSource } from '@angular/cdk/collections';
import { CdkDragMove } from '@angular/cdk/drag-drop';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Directive,
    ElementRef,
    HostBinding,
    HostListener,
    Inject,
    Input,
    OnInit,
    Optional,
    ViewEncapsulation,
    DOCUMENT
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DateRange } from 'src/app/_shared/modules/form-component/date-range/date-range.component';
import { NumberRangeType } from 'src/app/_shared/modules/form-component/number-range/number-range.component';
import { KalTableGenericDatasource } from 'src/app/_shared/modules/kal-table/classes/kal-table-generic-datasource';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import {
    KalTableSortDirection,
    KalTableSortEvent
} from 'src/app/_shared/modules/kal-table/classes/managers/kal-table-sort-manager';
import { KalTableComponent, KalTableDataSourceInput } from 'src/app/_shared/modules/kal-table/kal-table.component';
import { arrayEmpty } from 'src/app/utils/array.utils';

export enum ColumnFilterType {
    Text = 'text',
    Number = 'number',
    Date = 'date',
    List = 'list',
}

export type ColumnFilterValueType = string | string[] | NumberRangeType | DateRange;

export class ColumnFilter {
    columnName: string;
    filterValue?: ColumnFilterValueType;
    type?: ColumnFilterType;
    showsOnlyEmptyValues: boolean;
    valueAccessor?: (row, code) => string;
}

export class ColumnFilterListChoice {
    code: string;
    label: string;
    isDisplayed: boolean;
}

interface FilterForm {
    dateFilter: FormControl<ColumnFilterValueType | null>,
    textFilter: FormControl<ColumnFilterValueType | null>,
    numberfilter: FormControl<ColumnFilterValueType | null>,
    listFilter: FormArray<FormGroup<ChoiceForm>>,
    showEmptyValuesOnly: FormControl<boolean>,
}

interface ChoiceForm {
    selected: FormControl<boolean>,
    code: FormControl<string>,
    label: FormControl<string>,
}

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'kal-card.column-filters',
    exportAs: 'columnFilterPosition',
    standalone: false
})
export class ColumnFilterPositionDirective implements OnInit {

    constructor(private el: ElementRef<HTMLElement>) {
    }

    @HostListener('window:resize')
    setPosition(): void {
        const box = this.el.nativeElement;
        const parentPosition= box.parentElement.getBoundingClientRect();

        box.style.left = parentPosition.x + 'px';
        box.style.top = (parentPosition.top + box.parentElement.offsetHeight + 5) + 'px';
        box.style.right = '';
        const {x, width} = box.getBoundingClientRect();
        if (x + width + 5 > window.innerWidth) {
            box.style.left = '';
            box.style.right = '5px';
        }
    }

    ngOnInit(): void {
        this.setPosition();
    }
}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'th[kalTableHeader]',
    templateUrl: 'kal-table-header.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class KalTableHeaderComponent<T> implements OnInit {

    @Input() kalTableHeader: string;
    @Input() label: string;
    displayFiltersBox = false;
    form: FormGroup<FilterForm>;
    headerDragPosition = {x: 0, y: 0};
    protected readonly ColumnFilterType = ColumnFilterType;
    private _newWidth: number | null = null;
    private _baseWidth: number | null = null;
    private _initialWidth: number | null = null; // Width définie dans le CSS
    private _headerElement: HTMLElement | null = null; // élément <th> de la colonne concernée
    private _column: KalTableHeader;

    constructor(@Optional() private readonly table: KalTableComponent<T>,
                @Inject(DOCUMENT) private readonly document: Document,
                private readonly fb: FormBuilder,
                public readonly cdr: ChangeDetectorRef) {
    }

    get column(): KalTableHeader {
        if (!this._column) {
            this._column = this.table.getColumn(this.kalTableHeader) || new KalTableHeader();
            if (this.label) {
                this._column.label = this.label;
            }
        }

        if (!('columnFilterType' in this._column) && this.canHaveColumnFilter) {
            // Affichage par défaut : Filtre en mode texte
            this._column.columnFilterType = ColumnFilterType.Text;
        }

        return this._column;
    }

    get dataSource(): KalTableDataSourceInput<T> {
        return this.table.dataSource;
    }

    get sort(): KalTableSortEvent {
        if (isDataSource(this.dataSource) && this.dataSource.sort) {
            return this.dataSource.sort.value;
        }
        return null;
    }

    get canHaveColumnFilter(): boolean {
        return this.dataSource instanceof KalTableGenericDatasource;
    }

    get showEmptyValuesOnly(): boolean {
        return !!this.form?.value?.showEmptyValuesOnly;
    }

    get isFiltered(): boolean {
        if (!this.canHaveColumnFilter) {
            return false;
        }

        return (this.dataSource as KalTableGenericDatasource<T>).columnFilters
            .filter(columnFilter => columnFilter.columnName === this.column.code)
            .some(columnFilter => !!columnFilter.filterValue || !!columnFilter.showsOnlyEmptyValues);
    }

    get listFilter(): FormArray {
        return this.form.get('listFilter') as FormArray;
    }

    @HostBinding('class.sortable')
    get sortable(): boolean {
        return this.column.sortable === true;
    }

    @HostBinding('class.filterable')
    get filterable(): boolean {
        return !!this.canHaveColumnFilter && !!this.column.columnFilterType;
    }

    @HostBinding('class.sorted')
    get sorted(): boolean {
        if (isDataSource(this.dataSource)) {
            const {code} = this.sort || {code: null};
            return this.column.code === code;
        }
    }

    @HostBinding('class.asc')
    get sortedAsc(): boolean {
        if (isDataSource(this.dataSource)) {
            const {direction} = this.sort || {direction: null};
            return this.sorted && direction === KalTableSortDirection.ASC;
        }
    }

    @HostBinding('class.desc')
    get sortedDesc(): boolean {
        if (isDataSource(this.dataSource)) {
            const {direction} = this.sort || {direction: null};
            return this.sorted && direction === KalTableSortDirection.DESC;
        }
    }

    /**
     * Récupère le <th> de la colonne actuelle
     */
    get headerElement(): HTMLElement {
        if (!this._headerElement) {
            this._headerElement = this.document.querySelector(`.${this.kalColumnCode}`);
        }
        return this._headerElement;
    }

    private get kalColumnCode(): string {
        return 'kal-column-' + this.kalTableHeader;
    }

    sortByColumn(): void {
        if (isDataSource(this.dataSource) && this.column.sortable && this.sort) {
            const currentSort = this.sort;

            let direction = KalTableSortDirection.ASC;

            if (currentSort.code === this.column.code && currentSort.direction === direction) {
                direction = KalTableSortDirection.DESC;
            }

            this.dataSource.sort.set({
                code: this.column.code as string,
                direction,
                customComparator: this.column?.customComparator || null,
                valueAccessor: this.column?.exporter || null
            });
            this.cdr.markForCheck();
        }
    }

    toggleFilters(): void {
        if (!this.displayFiltersBox && this.column.columnFilterType === ColumnFilterType.List) {
            this.refreshListForm();
        }
        this.displayFiltersBox = !this.displayFiltersBox;
    }

    applyFilters(): void {
        this.displayFiltersBox = false;

        let value: ColumnFilterValueType = null;
        switch (this.column.columnFilterType) {
            case ColumnFilterType.Text:
                value = this.form.value.textFilter;
                break;
            case ColumnFilterType.Number:
                value = this.form.value.numberfilter;
                break;
            case ColumnFilterType.List:
                value = this.form.value.listFilter.filter(choice => !!choice.selected).map(choice => choice.code) as string[];
                if (arrayEmpty(value)) {
                    value = null;
                }
                break;
            case ColumnFilterType.Date:
                value = this.form.value.dateFilter;
                break;
        }

        if (this.dataSource instanceof KalTableGenericDatasource) {
            if (!value && !this.form.value.showEmptyValuesOnly) {
                this.dataSource.deleteColumnFilter(this.column.code.toString());
            } else {
                this.dataSource.setColumnFilter({
                    columnName: this.column.code.toString(),
                    filterValue: value,
                    type: this.column.columnFilterType,
                    showsOnlyEmptyValues: this.form.value.showEmptyValuesOnly,
                    valueAccessor: this.column.exporter,
                });
            }
        }
    }

    resetFilters(): void {
        if (this.dataSource instanceof KalTableGenericDatasource) {
            this.dataSource.deleteColumnFilter(this.column.code.toString());

            this.form.patchValue({
                dateFilter: null,
                textFilter: null,
                numberfilter: null,
                showEmptyValuesOnly: false
            });

            this.listFilter.controls.forEach((control: FormGroup<ChoiceForm>) => {
                control.controls.selected.patchValue(false);
            });

            this.displayFiltersBox = false;
        }
    }

    /**
     * Appelé lorsque l'utilisateur déplace le curseur de redimensionnement d'une colonne
     * @param $event - CdkDragMove
     */
    onResize($event: CdkDragMove): void {
        // cas du premier resize : on initialise les valeurs
        if (this._baseWidth === null && this._initialWidth === null) {
            const width = this.headerElement.getBoundingClientRect().width;
            this._baseWidth = width;
            this._initialWidth = width;
        }

        this._newWidth = Math.round(this._baseWidth + $event.distance.x);

        // Si la nouvelle largeur est inférieur à la largeur de colonne initialement paramétrée (dans le CSS par exemple)
        // on informe l'utilisateur en mettant la cellule du header en surbrillance
        this.toggleOverlapWarning(this._newWidth < this._initialWidth);

        this.headerElement.style.cssText = `--kal-th-width: ${this._newWidth}px !important; width: ${this._newWidth}px !important`;
    }

    /**
     * Appelé lorsque l'utilisateur relache le bouton de la souris après avoir redimensionné la colonne
     */
    onResizeEnd(): void {
        this.headerDragPosition = {x: 0, y: 0}; // Repositionne le curseur

        // On set la width de base comme étant désormais la nouvelle width post-redimensionnement
        // En contexte plein écran, il faut utiliser la valeur brute (baseWidth + distanceX du CdkDrag)
        // au lieu de la taille du boundingClientRect qui n'est plus viable
        this._baseWidth = this.table.isFullScreenContext ? this._newWidth : Math.round(this.headerElement.getBoundingClientRect().width);

        // On enlève la surbrillance rouge du dépassement
        this.toggleOverlapWarning(false);

        // 2 cas de figure pour les redimensionnement de colonnes des kal-table :
        // - 1) kal-table SANS virtual scroll => la width est synchronisée automatiquement.
        // - 2) kal-table AVEC virtual scroll => il faut actualiser la largeur des cellules manuellement
        const viewport: CdkVirtualScrollViewport = this.table.viewport;

        if (viewport) {
            this.headerElement.classList.toggle('resized', true);

            this.updateResizedHeaderList();
            this.table.render({recalculateColumnWidths: true});
        }
    }

    close(): void {
        this.displayFiltersBox = false;
    }

    private updateResizedHeaderList(): void {
        this.table.updateResizedHeaderList({
            code: this.kalColumnCode,
            currentWidth: this._baseWidth,
            initialWidth: this._initialWidth
        });
    }

    private toggleOverlapWarning(force: boolean): void {
        this.headerElement.classList.toggle('resize-overlap-warning', force);
    }

    private createChoiceForm(code: string, label: string, selected: boolean): FormGroup {
        return this.fb.group({
            selected: [selected],
            code: [code],
            label: [label],
        });
    }

    // todo améliorer pour faire un refresh uniquement quand y'a un setRows et pas à chaque fois qu'on clic
    private refreshListForm(): void {
        if (!this.canHaveColumnFilter || this.column.columnFilterType !== ColumnFilterType.List) {
            return;
        }
        this.form.setControl('listFilter', new FormArray([]));

        const filterValue =
            (this.dataSource as KalTableGenericDatasource<T>).columnFilters.find(f => f.columnName === this.column.code)?.filterValue || null;
        const columnFilterList = this.column.columnFilterList || this.column.dynamicColumnFilterList;

        columnFilterList
            .filter(choice => choice.isDisplayed)
            .forEach(choice => {
                const selected = this.column.columnFilterType === ColumnFilterType.List
                    ? (filterValue as string[])?.includes(choice.code)
                    : false;
                this.listFilter.push(this.createChoiceForm(choice.code, choice.label, selected));
            });
    }

    ngOnInit(): void {

        if (!this.kalTableHeader) {
            throw Error('missing @Input [kalTableHeader] ');
        }

        if (this.canHaveColumnFilter) {

            const {type, filterValue} =
            (this.dataSource as KalTableGenericDatasource<T>).columnFilters.find(f => f.columnName === this.column.code) || {};

            const valueOrDefault = (filterType: ColumnFilterType) => type === filterType ? filterValue : null;

            this.form = this.fb.group<FilterForm>({
                dateFilter: this.fb.control(valueOrDefault(ColumnFilterType.Date)),
                textFilter: this.fb.control(valueOrDefault(ColumnFilterType.Text)),
                numberfilter: this.fb.control(valueOrDefault(ColumnFilterType.Number)),
                listFilter: new FormArray([]),
                showEmptyValuesOnly: this.fb.control(false)
            });
        }

    }

}
